import React from "react";

// ─────────────────────────────────────────────────────────────────────────────
// Render del cuerpo de una FAQ.
//
// El backend guarda `content` como TEXTO PLANO escrito a mano por soporte desde
// /admin/faqs: saltos de línea, viñetas con guión, pasos numerados (a veces con
// emojis de teclado tipo 1️⃣) y URLs sueltas. La vista lo volcaba entero en un
// <p> con `white-space: pre-wrap`, así que la estructura se veía pero no
// EXISTÍA: sin listas y, sobre todo, con los enlaces como texto muerto — la
// respuesta de MercadoLibre dice "haz clic en el siguiente enlace" y no había
// nada que clicar.
//
// Se parsea a bloques y se construyen nodos de React. NUNCA se inyecta HTML
// (nada de dangerouslySetInnerHTML): el contenido lo escribe un humano en un
// panel de administración, o sea que es entrada no confiable para esta vista.
// El esquema del enlace no sale jamás del texto; se deriva de la forma del
// token, así que un "javascript:..." no matchea y queda como texto plano.
// ─────────────────────────────────────────────────────────────────────────────

const BULLET_RE = /^\s*[-–—•*]\s+(.*)$/;

// Numeración normal ("1." / "2)") o emoji de teclado ("1️⃣").
//
// El separador tras el punto es OBLIGATORIO (`\s+`). Sin él, cualquier cifra
// decimal o con separador de miles al principio de una línea se comía sus
// primeros dígitos: "5.000 CLP hasta 10 kg" se renderizaba como el ítem
// "000 CLP hasta 10 kg". En una FAQ de logística —tarifas, pesos máximos— eso
// es mostrar un número equivocado sin ninguna señal de error.
const ORDERED_RE = /^\s*(?:(\d{1,2})[.)]\s+|([0-9])️?⃣\s*)(.*)$/;

// Una línea pegada a un ítem de lista (sin renglón en blanco de por medio) se
// considera su continuación SOLO si no arranca como prosa nueva: un símbolo,
// una flecha o una URL. Así se recupera el enlace que en la FAQ de
// MercadoLibre va bajo el paso 1, sin tragarse la frase de cierre que suele
// seguir a una lista ("Recuerda confirmar.") y que sí es un párrafo aparte.
const CONTINUATION_RE = /^(?:[^\p{L}\p{N}]|https?:\/\/|www\.)/iu;

const URL_RE =
  /(https?:\/\/[^\s<>()]*[^\s<>().,;:!?]|www\.[^\s<>()]*[^\s<>().,;:!?]|[\w.+-]+@[\w-]+\.[\w.-]*[\w-])/g;

/**
 * Parte el texto plano en bloques.
 *   { type: 'p',  items: [texto] }
 *   { type: 'ul', items: [...] }
 *   { type: 'ol', items: [...], start: n }   ← n = número que escribió el autor
 * Los `p` y los ítems conservan sus saltos internos (se pintan con `pre-wrap`).
 */
export function parseFaqContent(raw) {
  // `String(...)` y no un chequeo de tipo: un `content` numérico llegado de la
  // base tiene que mostrarse igual, no desaparecer en silencio.
  const text = raw === null || raw === undefined ? "" : String(raw);
  if (!text.trim()) return [];

  const blocks = [];
  let paragraph = [];
  let inListItem = false;

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ type: "p", items: [paragraph.join("\n")] });
      paragraph = [];
    }
  };

  const pushListItem = (type, item, number) => {
    flushParagraph();
    const last = blocks[blocks.length - 1];
    if (last && last.type === type) {
      last.items.push(item);
      return;
    }
    const block = { type, items: [item] };
    // La numeración se respeta tal cual: una lista que arranca en "3)" tiene
    // que renderizar 3, o un "repite el paso 3" apunta al ítem equivocado.
    if (type === "ol") block.start = Number.isFinite(number) ? number : 1;
    blocks.push(block);
  };

  for (const line of text.split("\n")) {
    const trimmed = line.trim();

    if (!trimmed) {
      flushParagraph();
      inListItem = false;
      continue;
    }

    const ordered = line.match(ORDERED_RE);
    // Un "-" o "1." sin texto detrás no es un ítem de lista, es un guión suelto.
    if (ordered && ordered[3].trim()) {
      pushListItem("ol", ordered[3].trim(), parseInt(ordered[1] ?? ordered[2], 10));
      inListItem = true;
      continue;
    }

    const bullet = line.match(BULLET_RE);
    if (bullet && bullet[1].trim()) {
      pushListItem("ul", bullet[1].trim());
      inListItem = true;
      continue;
    }

    if (inListItem && CONTINUATION_RE.test(trimmed)) {
      const last = blocks[blocks.length - 1];
      last.items[last.items.length - 1] += `\n${trimmed}`;
      continue;
    }

    inListItem = false;
    paragraph.push(line);
  }
  flushParagraph();

  return blocks;
}

/** Deriva un href seguro de la FORMA del token; nunca del esquema que traiga. */
function hrefFor(token) {
  if (/^https?:\/\//i.test(token)) return token;
  if (/^www\./i.test(token)) return `https://${token}`;
  if (token.includes("@")) return `mailto:${token}`;
  return null;
}

/** Convierte URLs y correos sueltos del texto en enlaces reales. */
export function linkify(text, keyPrefix = "l") {
  const parts = String(text ?? "").split(URL_RE);
  return parts.map((part, i) => {
    // Los índices impares son las capturas del separador: los candidatos.
    const href = i % 2 === 1 ? hrefFor(part) : null;
    if (!href) return part;
    const external = !href.startsWith("mailto:");
    return (
      <a
        key={`${keyPrefix}-${i}`}
        href={href}
        className="cfaq__link"
        {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
      >
        {part}
      </a>
    );
  });
}

/**
 * Resalta las coincidencias de `query` dentro de `text`.
 * Sin regex construida con la entrada del usuario: se busca por índice para no
 * tener que escapar metacaracteres ni arriesgar un patrón catastrófico.
 */
export function highlight(text, query) {
  const source = String(text ?? "");
  const needle = String(query ?? "").trim().toLowerCase();
  if (!needle) return source;

  const haystack = source.toLowerCase();
  const nodes = [];
  let from = 0;
  let at = haystack.indexOf(needle);
  while (at !== -1) {
    if (at > from) nodes.push(source.slice(from, at));
    nodes.push(
      <mark key={`hl-${at}`} className="cfaq__mark">
        {source.slice(at, at + needle.length)}
      </mark>,
    );
    from = at + needle.length;
    at = haystack.indexOf(needle, from);
  }
  if (from < source.length) nodes.push(source.slice(from));
  return nodes;
}

/** Enlaces vivos + resaltado de la búsqueda sobre el mismo texto. */
function renderInline(text, query, keyPrefix) {
  const parts = linkify(text, keyPrefix);
  if (!String(query ?? "").trim()) return parts;
  return parts.map((part, i) =>
    typeof part === "string" ? (
      <React.Fragment key={`${keyPrefix}-h-${i}`}>
        {highlight(part, query)}
      </React.Fragment>
    ) : (
      part
    ),
  );
}

/**
 * Cuerpo de la respuesta: párrafos, listas y enlaces vivos.
 * `query` resalta las coincidencias DENTRO de la respuesta: sin eso, una
 * respuesta que se abre sola porque su texto coincidió no muestra dónde.
 */
export function FaqAnswer({ text, query = "" }) {
  const blocks = parseFaqContent(text);
  if (!blocks.length) return null;

  return (
    <div className="cfaq__a-body">
      {blocks.map((block, i) => {
        if (block.type === "p") {
          return (
            <p className="cfaq__a-text" key={`b-${i}`}>
              {renderInline(block.items[0], query, `b-${i}`)}
            </p>
          );
        }
        const List = block.type === "ol" ? "ol" : "ul";
        return (
          <List
            className={`cfaq__a-list cfaq__a-list--${block.type}`}
            key={`b-${i}`}
            {...(block.type === "ol" ? { start: block.start } : {})}
          >
            {block.items.map((item, j) => (
              <li key={`b-${i}-${j}`}>{renderInline(item, query, `b-${i}-${j}`)}</li>
            ))}
          </List>
        );
      })}
    </div>
  );
}

export default FaqAnswer;
